import axios, { type AxiosError } from 'axios';

export const GENERIC_API_ERROR_PATH = '/error';

/**
 * Teach Axios about the opt-out so callers can pass it in a request config without a cast, and so
 * a typo is a compile error rather than a silently ignored property.
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Suppress the generic-error-page redirect for this request. Set it when the caller renders the
     * failure itself — navigating away would unmount that UI along with any unsaved work.
     */
    skipGenericErrorRedirect?: boolean;
  }
}

export type ApiRequestConfigWithRedirectFlag = {
  skipGenericErrorRedirect?: boolean;
};

/**
 * Statuses that are a *semantic outcome* of a conditional or lifecycle-aware write, never an
 * "the API is unreachable" failure:
 *
 * - `409` — ownership/lifecycle conflict (`OWNERSHIP_CONFLICT`, `SUBMITTED_READONLY`,
 *   `SUBMISSION_ALREADY_EXISTS`, `INVALID_LIFECYCLE_TRANSITION`).
 * - `412` — optimistic-concurrency rejection (`PRECONDITION_FAILED`): a lost If-Match race.
 *
 * Both have a designed in-place treatment (LockTooltip ownership chrome, the submit-failure
 * notification, an autosave retry), so leaving the page for the generic error screen throws away
 * the diagnosis *and* the user's unsaved work. The generic page is for failures with no in-form
 * recovery — 5xx and transport errors.
 *
 * This is an app-wide policy, not exam-form-specific: a conflict or a failed precondition is an
 * outcome the caller is expected to read, on any route. Callers still opt out per request via
 * `skipGenericErrorRedirect` for the statuses NOT listed here (notably 403/404), which can
 * legitimately mean "misconfigured or unreachable" elsewhere.
 */
const NON_REDIRECT_OUTCOME_STATUSES = new Set([409, 412]);

/**
 * Whether a failed API call should send the user to the generic error page.
 * Skips auth/validation outcomes, conditional-write outcomes, and explicit per-request opt-out.
 */
export function shouldTriggerGenericApiErrorRedirect(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  const axiosError = error as AxiosError;
  const cfg = axiosError.config as ApiRequestConfigWithRedirectFlag | undefined;
  if (cfg?.skipGenericErrorRedirect) {
    return false;
  }
  if (axiosError.code === 'ERR_CANCELED') {
    return false;
  }

  const status = axiosError.response?.status;
  if (status === 401 || status === 400 || status === 422) {
    return false;
  }
  if (status != null && NON_REDIRECT_OUTCOME_STATUSES.has(status)) {
    return false;
  }

  if (!axiosError.response) {
    return Boolean(axiosError.request);
  }

  return true;
}

let redirectFn: (() => void) | undefined;

export function registerGenericApiErrorRedirect(fn: () => void): void {
  redirectFn = fn;
}

export function unregisterGenericApiErrorRedirect(): void {
  redirectFn = undefined;
}

export function triggerGenericApiErrorRedirect(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.location.pathname === GENERIC_API_ERROR_PATH) {
    return;
  }
  if (redirectFn) {
    redirectFn();
  } else {
    window.location.assign(GENERIC_API_ERROR_PATH);
  }
}
